import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "components/core/theme-provider";
import { useTranslation } from "react-i18next";
import { useDocumentFilesQuery } from "features/file-manager/hooks/use-document-manager";
import { getFileTypeIcon, getFileTypeInfo, PaginationState } from "features/file-manager/utils/file-manager";
import { Button } from "components/ui/button";
import { StatusBadge } from "features/file-manager/components/status-badge";
import { VectorizationStatus } from "features/file-manager/services/document-manager-service";
import { ArrowDown, ArrowUp, BookOpenTextIcon, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { SortingState } from "@tanstack/react-table";
import { Tabs, TabsList, TabsTrigger } from "components/ui/tabs";


interface SourceDocumentSelectorProps {
    onSelect?: (docs: any[]) => void;
}

export const SourceDocumentSelector: React.FC<
    SourceDocumentSelectorProps
> = ({ onSelect }) => {
    const { theme } = useTheme();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'my-files' | 'shared'>('my-files');

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState("");
    const [sortingState, setSortingState] = useState<SortingState>([]);
    const [selectedFilesMap, setSelectedFilesMap] = useState<Map<string, any>>(new Map());
    const [paginationState, setPaginationState] =
        useState<PaginationState>({
            pageIndex: 0,
            pageSize: 10,
            totalCount: 0,
        });

    useEffect(() => {
        // Reset pagination when tab changes
        setPaginationState((prev) => ({
            ...prev,
            pageIndex: 0,
        }));

        // Reset selected files when tab changes
        setSelectedIds(new Set());

        // Reset sorting state when tab changes
        setSortingState([]);

        // Reset search and search field when tab changes
        setSearch('');

    }, [activeTab]);

    const queryParams = useMemo(
        () => ({
            page: paginationState.pageIndex,
            pageSize: paginationState.pageSize,
            filter: {
                name: search,
            },
            sort: sortingState
        }),
        [paginationState.pageIndex, paginationState.pageSize, search, sortingState]
    );

    const { data, isLoading } =
        useDocumentFilesQuery(queryParams, {
            sharedOnly: activeTab === 'my-files' ? false : true,
        });

    const files = data?.data ?? [];
    const totalCount = data?.totalCount ?? 0;

    const totalPages = Math.ceil(
        totalCount / paginationState.pageSize
    );
    const currentPage = paginationState.pageIndex;

    const toggleSelect = (file: any) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);

            if (next.has(file.id)) {
                next.delete(file.id);
                setSelectedFilesMap((m) => {
                    const copy = new Map(m);
                    copy.delete(file.id);
                    return copy;
                });
            } else {
                next.add(file.id);
                setSelectedFilesMap((m) => {
                    const copy = new Map(m);
                    copy.set(file.id, file);
                    return copy;
                });
            }

            return next;
        });
    };


    const goToPage = (page: number) => {
        setPaginationState((prev) => ({
            ...prev,
            pageIndex: page,
        }));
    };

    const goPrev = () => {
        if (currentPage > 0) {
            goToPage(currentPage - 1);
        }
    };

    const goNext = () => {
        if (currentPage < totalPages - 1) {
            goToPage(currentPage + 1);
        }
    };

    const visiblePages = useMemo(() => {
        const pages: number[] = [];
        const start = Math.max(0, currentPage - 1);
        const end = Math.min(totalPages - 1, start + 2);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        return pages;
    }, [currentPage, totalPages]);


    const handleSave = () => {
        onSelect?.(Array.from(selectedFilesMap.values()));
        setOpen(false);
        setSearch('');
        setPaginationState((prev) => ({
            ...prev,
            pageIndex: 0,
        }));
        setSelectedIds(new Set());
        setSelectedFilesMap(new Map());
        setSelectedFileName(null);
    };



    const toggleSortOrder = (columnId: 'name' | 'fileType' | 'lastModified') => {
        setSortingState((prev) => {
            const currentSort = prev[0];

            if (!currentSort || currentSort.id !== columnId) {
                return [{ id: columnId, desc: false }];
            } else if (currentSort.desc === false) {
                return [{ id: columnId, desc: true }];
            } else if (currentSort.desc === true) {
                return [];
            } else {
                return [{ id: columnId, desc: false }];
            }
        });
    };

    const currentNameSort = sortingState.find(s => s.id === 'name');
    // const currentTypeSort = sortingState.find(s => s.id === 'fileType');
    const currentLastModifiedSort = sortingState.find(s => s.id === 'lastModified');

    return (
        <>
            <div
                onClick={() => setOpen(true)}
                className="text-sm font-medium border border-primary sm:px-3 px-1.5 py-1.5 h-[34px] min-w-[34px] flex justify-center items-center rounded-full cursor-pointer hover:bg-muted text-primary truncate max-w-[240px]"
                title={selectedFileName ?? t("SELECT_DOCUMENT")}
            >
                <BookOpenTextIcon className="w-4 h-4 text-primary block xl:hidden" />
                <span className="hidden xl:block">{t("SELECT_DOCUMENT")}</span>
            </div>

            {/* -------------------- Modal -------------------- */}
            {open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                    <div
                        className={`w-[720px] max-w-[90vw] rounded-xl shadow-xl ${theme === "dark"
                            ? "bg-background"
                            : "bg-white"
                            }`}
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h2 className="text-lg font-semibold truncate">
                                {t("SELECT_DOCUMENT")}
                            </h2>
                            <button
                                onClick={() => {
                                    setOpen(false);
                                    setSearch('');
                                    setPaginationState((prev) => ({
                                        ...prev,
                                        pageIndex: 0,
                                    }));
                                    setSelectedIds(new Set());
                                    setSelectedFilesMap(new Map());
                                    setSelectedFileName(null);
                                }}
                                className="text-muted-foreground hover:text-foreground text-xl"
                            >
                                ×
                            </button>
                        </div>

                        <div className="px-6 pt-4">
                            <Tabs
                                value={activeTab}
                                onValueChange={(value) =>
                                    setActiveTab(value as 'my-files' | 'shared')
                                }
                            >
                                <TabsList className="border border-primary p-1 rounded-lg font-medium">
                                    <TabsTrigger
                                        value="my-files"
                                        className="data-[state=active]:bg-primary data-[state=active]:text-white text-primary rounded-lg px-[12px] py-[6px]"
                                    >
                                        {t("MY_FILES")}
                                    </TabsTrigger>

                                    <TabsTrigger
                                        value="shared"
                                        className="data-[state=active]:bg-primary data-[state=active]:text-white text-primary rounded-lg px-[12px] py-[6px]"
                                    >
                                        {t("SHARED_WITH_ME")}
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>



                        <div className="flex gap-3 px-6 py-4 border-b">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary" />
                                <input
                                    value={search}
                                    placeholder={t("SEARCH_DOCUMENTS")}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setPaginationState((prev) => ({
                                            ...prev,
                                            pageIndex: 0,
                                        }));
                                    }}
                                    className="
                                        h-10 w-full rounded-3xl
                                        border border-primary
                                        bg-background pl-9 pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    "
                                />
                            </div>
                        </div>

                        <div className="max-h-[40vh] overflow-y-auto">
                            {/* Column Headers */}
                            <div className="grid grid-cols-[1fr_auto_auto] gap-4 items-center px-6 py-2.5 bg-muted sticky top-0 z-10">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">
                                        {t("NAME")}
                                    </span>
                                    <button
                                        onClick={() => toggleSortOrder('name')}
                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {currentNameSort ? (
                                            currentNameSort.desc ? (
                                                <ArrowDown className="w-4 h-4" />
                                            ) : (
                                                <ArrowUp className="w-4 h-4" />
                                            )
                                        ) : (
                                            <ChevronsUpDown className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>

                                <div className="flex items-center gap-2 justify-end">
                                    <span className="text-sm text-muted-foreground">
                                        {t("LAST_MODIFIED")}
                                    </span>
                                    <button
                                        onClick={() => toggleSortOrder('lastModified')}
                                        className="text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        {currentLastModifiedSort ? (
                                            currentLastModifiedSort.desc ? (
                                                <ArrowDown className="w-4 h-4" />
                                            ) : (
                                                <ArrowUp className="w-4 h-4" />
                                            )
                                        ) : (
                                            <ChevronsUpDown className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="h-auto">
                                {isLoading && (
                                    <div className="flex items-center justify-center h-[40vh]">
                                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                                    </div>
                                )}

                                {!isLoading && files.length === 0 && (
                                    <div className="text-sm text-muted-foreground flex items-center justify-center h-[40vh]">
                                        {activeTab === 'my-files'
                                            ? t("NO_DOCUMENTS_FOUND")
                                            : t("NO_SHARED_DOCUMENTS")}
                                    </div>
                                )}

                                {!isLoading &&
                                    files.map((file: any) => (
                                        <Row
                                            key={file.id}
                                            name={file.name}
                                            vectorizationStatus={file.vectorizationStatus}
                                            fileType={file.fileType || "file"}
                                            lastModified={file.lastModified}
                                            selected={selectedIds.has(file.id)}
                                            onClick={() => {
                                                setSelectedFileName(file.name);
                                                toggleSelect(file);
                                            }}
                                        />
                                    ))}
                            </div>
                        </div>


                        <div className="flex items-center justify-between px-6 py-4 border-t">
                            <span className="text-sm text-muted-foreground">
                                {t("SHOWING")}{" "}
                                {files.length
                                    ? currentPage *
                                    paginationState.pageSize +
                                    1
                                    : 0}
                                –
                                {currentPage *
                                    paginationState.pageSize +
                                    files.length}{" "}
                                of {totalCount}
                            </span>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={goPrev}
                                    disabled={currentPage === 0}
                                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50"
                                >
                                    {t("PREV")}
                                </button>

                                {visiblePages.map((page) => (
                                    <button
                                        key={page}
                                        onClick={() => goToPage(page)}
                                        className={`px-3 py-1 border rounded-md text-sm ${page === currentPage
                                            ? "bg-muted font-medium"
                                            : ""
                                            }`}
                                    >
                                        {page + 1}
                                    </button>
                                ))}

                                <button
                                    onClick={goNext}
                                    disabled={currentPage >= totalPages - 1}
                                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50"
                                >
                                    {t("NEXT")}
                                </button>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end my-4 px-6">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setOpen(false);
                                    setSearch('');
                                    setPaginationState((prev) => ({
                                        ...prev,
                                        pageIndex: 0,
                                    }));
                                    setSelectedIds(new Set());
                                    setSelectedFilesMap(new Map());
                                    setSelectedFileName(null);
                                }}
                                className="px-6 py-3 text-sm font-medium border rounded-lg"
                            >
                                {t("CANCEL")}
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={selectedIds.size === 0}
                                className="px-6 py-3 text-sm font-medium bg-primary text-primary-foreground rounded-lg"
                            >
                                {selectedIds.size ? t("DONE") + ` (${selectedIds.size})` : t("DONE")}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const Row = ({
    name,
    vectorizationStatus,
    fileType,
    lastModified,
    selected,
    onClick,
}: {
    icon?: string;
    name: string;
    vectorizationStatus?: VectorizationStatus;
    fileType: string;
    lastModified?: string | Date;
    selected?: boolean;
    onClick?: () => void;
}) => {

    const IconComponent = getFileTypeIcon(fileType);
    const { iconColor, backgroundColor } = getFileTypeInfo(fileType);
    // const { theme } = useTheme();
    const formatDate = (date: string | Date | undefined) => {
        if (!date) return '-';
        const dateObj = typeof date === 'string' ? new Date(date) : date;

        const day = String(dateObj.getDate()).padStart(2, '0');
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const year = dateObj.getFullYear();

        return `${day}.${month}.${year}`;
    };

    return (
        <div
            onClick={onClick}
            className={`grid grid-cols-[1fr_auto_auto] gap-4 items-center px-6 py-3 border-b cursor-pointer ${selected ? "bg-sidebar-accent" : "hover:bg-muted"
                }`}
        >
            <div className="flex items-center gap-2 min-w-0">
                <div className={`h-8 w-8 flex-shrink-0 rounded-lg flex items-center justify-center ${backgroundColor}`}>
                    <IconComponent className={`w-4 h-4 ${iconColor}`} />
                </div>
                <span className={`text-sm truncate ${selected ? "" : ""}`}>{name}</span>
                {vectorizationStatus && (
                    <StatusBadge status={vectorizationStatus} />
                )}
            </div>
            <div className="flex items-center justify-end">
                <span className={`text-sm ${selected ? "" : ""}`}>{formatDate(lastModified)}</span>
            </div>
        </div>
    );
};